import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Info, LayoutDashboard, MapPin, Search, Ticket, X } from "lucide-react";
import { SignedIn, SignedOut, UserButton, useUser } from "@clerk/clerk-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import LocationModal from "./LocationModal";
import { LocationContext } from "../../contexts/LocationContext";
import api from "../../services/api";
import MobileBottomNav from "./MobileBottomNav";
import BrandLogo from "./BrandLogo";
import { Dialog, SwipeableDrawer } from "@mui/material";
import "./CustomNavbar.css";

const NAV_TABS = [
  { label: "My Movies", path: "/movies" },
  { label: "List Your Show", path: "/partner", partnerCTA: true },
  { label: "My Bookings", path: "/my-bookings", authOnly: true },
  { label: "About", path: "/about" },
  { label: "Dashboard", path: "/admin", adminOnly: true },
];

const formatLocation = (locationData) => {
  if (!locationData) {
    return {
      primary: "Select Location",
      secondary: "Choose your city",
    };
  }

  const city = locationData.city || "Select Location";
  const secondaryParts = [];

  if (locationData.state) secondaryParts.push(locationData.state);
  if (locationData.country) secondaryParts.push(locationData.country);

  return {
    primary: city,
    secondary: secondaryParts.join(", ") || "Choose your city",
  };
};

const normalizeMovieList = (payload) => {
  const rawList = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.movies)
      ? payload.movies
      : [];

  return rawList
    .filter((movie) => movie?._id && movie?.title)
    .map((movie) => ({
      _id: movie._id,
      title: movie.title,
      poster: movie.poster || movie.posterUrl || "",
      language: Array.isArray(movie.language)
        ? movie.language.join(", ")
        : movie.language || "Language TBA",
      certification: movie.certification || movie.censorRating || "UA",
      duration: movie.duration || movie.runtime || null,
    }));
};

const movieDurationLabel = (minutes) => {
  const duration = Number(minutes);
  if (!duration || Number.isNaN(duration)) return "Runtime TBA";

  const hrs = Math.floor(duration / 60);
  const mins = duration % 60;
  if (!hrs) return `${mins} min`;
  if (!mins) return `${hrs} hr`;
  return `${hrs} hr ${mins} min`;
};

const CustomNavbar = () => {
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [locationLabel, setLocationLabel] = useState(formatLocation(null));
  const [scrolled, setScrolled] = useState(false);
  const [movies, setMovies] = useState([]);
  const [loadingMovies, setLoadingMovies] = useState(false);
  const [moviesError, setMoviesError] = useState("");
  const [hasFetchedMovies, setHasFetchedMovies] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  const searchInputRef = useRef(null);
  const headerRef = useRef(null);
  const { user } = useUser();
  const { userLocation } = useContext(LocationContext);
  const navigate = useNavigate();
  const currentRoute = useLocation();

  const visibleTabs = useMemo(() => {
    return NAV_TABS.filter((tab) => {
      if (tab.adminOnly) {
        return user?.publicMetadata?.role === "admin";
      }
      return true;
    });
  }, [user]);

  const movieHints = useMemo(() => {
    if (!movies.length) return ["Search movies"];
    return movies.slice(0, 5).map((movie) => movie.title);
  }, [movies]);

  const [hintIndex, setHintIndex] = useState(0);

  useEffect(() => {
    if (userLocation?.city) {
      setLocationLabel(formatLocation(userLocation));
      return;
    }

    const saved = localStorage.getItem("userLocation");
    if (saved) {
      try {
        setLocationLabel(formatLocation(JSON.parse(saved)));
      } catch (error) {
        console.error("Unable to parse saved location", error);
      }
    }
  }, [userLocation]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (showSearchModal) {
      document.body.classList.add("modal-open");
      document.documentElement.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
      document.documentElement.classList.remove("modal-open");
    }
    return () => {
      document.body.classList.remove("modal-open");
      document.documentElement.classList.remove("modal-open");
    };
  }, [showSearchModal]);

  useEffect(() => {
    if (!headerRef.current) return undefined;

    const updateHeaderHeight = () => {
      if (!headerRef.current) return;
      const height = Math.ceil(
        headerRef.current.getBoundingClientRect().height,
      );
      document.documentElement.style.setProperty(
        "--district-header-height",
        `${height}px`,
      );
    };

    updateHeaderHeight();
    window.addEventListener("resize", updateHeaderHeight);

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => {
        window.requestAnimationFrame(() => {
          updateHeaderHeight();
        });
      });
      observer.observe(headerRef.current);
    }

    return () => {
      window.removeEventListener("resize", updateHeaderHeight);
      observer?.disconnect();
    };
  }, []);



  useEffect(() => {
    if (!showSearchModal || hasFetchedMovies) return;

    const fetchMovies = async () => {
      setLoadingMovies(true);
      setMoviesError("");

      try {
        const response = await api.get("/movies", {
          params: { isAdmin: false },
        });
        setMovies(normalizeMovieList(response.data));
      } catch (error) {
        console.error("Failed to load movies for navbar search", error);
        setMoviesError("Unable to load movies right now.");
      } finally {
        setHasFetchedMovies(true);
        setLoadingMovies(false);
      }
    };

    fetchMovies();
  }, [showSearchModal, hasFetchedMovies]);

  useEffect(() => {
    if (!showSearchModal || searchTerm.trim()) return undefined;

    const ticker = setInterval(() => {
      setHintIndex((prev) => (prev + 1) % movieHints.length);
    }, 2200);

    return () => clearInterval(ticker);
  }, [showSearchModal, searchTerm, movieHints]);

  useEffect(() => {
    if (!showSearchModal) return undefined;

    const frameId = requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });

    return () => cancelAnimationFrame(frameId);
  }, [showSearchModal]);

  const filteredMovies = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    const list = movies.filter((movie) => {
      if (!query) return true;

      return (
        movie.title.toLowerCase().includes(query) ||
        movie.language.toLowerCase().includes(query)
      );
    });

    return list.sort((left, right) => left.title.localeCompare(right.title));
  }, [movies, searchTerm]);

  const openSearchModal = () => {
    setSearchTerm("");
    setShowSearchModal(true);
  };

  const handleTabClick = (tab) => {
    if (tab.authOnly && !user) {
      navigate("/sign-in");
      return;
    }

    navigate(tab.path);
  };

  const handleMovieClick = (movieId) => {
    setShowSearchModal(false);
    navigate(`/movies/${movieId}`);
  };

  const isTabActive = (tab) => currentRoute.pathname.startsWith(tab.path);

  return (
    <div className="district-nav-shell">

      <header
        ref={headerRef}
        id="master-header"
        className={`district-master-header ${scrolled ? "scrolled" : ""}`}
      >
        <div className="district-head-wrap">
          <div className="district-head-row">
            <div className="district-head-left">
              <Link to="/" className="district-brand-link" aria-label="CineBook">
                <BrandLogo className="district-navbar-logo" />
              </Link>

              <span className="district-header-divider" />

              <button
                type="button"
                className="district-location-trigger"
                onClick={() => setShowLocationModal(true)}
                aria-label="Choose location"
              >
                <span className="district-location-icon-wrap">
                  <MapPin size={20} strokeWidth={2} />
                </span>
                <span className="district-location-copy">
                  <strong>{locationLabel.primary}</strong>
                  <small>{locationLabel.secondary}</small>
                </span>
              </button>
            </div>

            <div className="district-head-center hide-on-mobile">
              <nav
                className="district-category-tabs"
                aria-label="Primary navigation tabs"
              >
                {visibleTabs.map((tab) => (
                  <button
                    type="button"
                    key={tab.label}
                    className={`district-category-tab ${isTabActive(tab) ? "active" : ""}`}
                    onClick={() => handleTabClick(tab)}
                  >
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>

            <div className="district-head-right">
              <button
                type="button"
                className="district-action-icon"
                onClick={openSearchModal}
                aria-label="Open movie search"
              >
                <Search size={18} />
              </button>


              <div className="hide-on-mobile">
                <SignedOut>
                  <div className="district-auth-actions">
                    <Link
                      to="/sign-in"
                      className="district-auth-btn district-auth-outline"
                    >
                      Login
                    </Link>
                    <Link
                      to="/sign-up"
                      className="district-auth-btn district-auth-solid"
                    >
                      Sign Up
                    </Link>
                  </div>
                </SignedOut>

                <SignedIn>
                  <div className="district-user-button">
                    <UserButton
                      appearance={{
                        elements: {
                          avatarBox: "district-user-avatar-box",
                        },
                      }}
                    >
                      <UserButton.MenuItems>
                        <UserButton.Action
                          label={`${locationLabel.primary}, ${locationLabel.secondary}`}
                          labelIcon={<MapPin size={16} />}
                          onClick={() => setShowLocationModal(true)}
                        />
                        <UserButton.Link
                          label="My Bookings"
                          href="/my-bookings"
                          labelIcon={<Ticket size={16} />}
                        />
                        <UserButton.Link
                          label="About"
                          href="/about"
                          labelIcon={<Info size={16} />}
                        />
                        {user?.publicMetadata?.role === "admin" && (
                          <UserButton.Link
                            label="Admin Dashboard"
                            href="/admin"
                            labelIcon={<LayoutDashboard size={16} />}
                          />
                        )}
                        <UserButton.Link
                          label="Partner Hub"
                          href="/partner"
                          labelIcon={<LayoutDashboard size={16} />}
                        />
                      </UserButton.MenuItems>
                    </UserButton>
                  </div>
                </SignedIn>
              </div>

              <div className="show-on-mobile mobile-top-profile">
                <SignedIn>
                  <button
                    className="mobile-avatar-btn"
                    onClick={() => window.dispatchEvent(new CustomEvent('open-mobile-profile'))}
                  >
                    <img
                      src={user?.imageUrl}
                      alt="Profile"
                      className="mobile-avatar-img"
                    />
                  </button>
                </SignedIn>
                <SignedOut>
                  <Link to="/sign-in" className="mobile-avatar-btn">
                    <UserButton />
                  </Link>
                </SignedOut>
              </div>
            </div>
          </div>

          <button
            type="button"
            className="district-inline-search"
            onClick={openSearchModal}
            aria-label="Search movies"
          >
            <Search size={16} />
            <span>Search for movies</span>
          </button>
        </div>
      </header>

      <LocationModal
        isOpen={showLocationModal}
        onClose={() => setShowLocationModal(false)}
      />

      {showSearchModal && (() => {
        const modalContent = (
          <div
            className="district-search-modal"
            style={{ margin: isMobile ? 0 : 'auto', width: '100%', maxWidth: '960px', height: '100%' }}
          >
            {isMobile && <div className="sheet-handle" style={{ marginTop: 12, marginBottom: 4 }} />}
            <div className="district-search-fixed">
              <div className="district-search-head">
                <h3 id="district-search-title">Search Movies</h3>
                <button
                  type="button"
                  className="district-search-close"
                  onClick={() => setShowSearchModal(false)}
                  aria-label="Close search"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="district-search-input-wrap">
                <Search size={18} className="district-search-input-icon" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={`Search for '${movieHints[hintIndex] || "movie"}'`}
                  className="district-search-input"
                  autoFocus
                />
              </div>
            </div>

            <section className="district-search-results" data-lenis-prevent>
              <h4 className="district-search-results-title">All Movies</h4>

              {loadingMovies ? (
                <div className="district-search-status">Loading movies...</div>
              ) : moviesError ? (
                <div className="district-search-status error">
                  {moviesError}
                </div>
              ) : (
                <div className="district-result-grid">
                  {filteredMovies.length ? (
                    filteredMovies.map((movie) => (
                      <button
                        type="button"
                        key={movie._id}
                        className="district-result-card"
                        onClick={() => handleMovieClick(movie._id)}
                      >
                        <img
                          src={
                            movie.poster ||
                            "https://via.placeholder.com/120x160"
                          }
                          alt={movie.title}
                          className="district-result-thumb"
                        />
                        <span className="district-result-copy">
                          <strong>{movie.title}</strong>
                          <small>{`${movie.certification} • ${movie.language} • ${movieDurationLabel(movie.duration)}`}</small>
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="district-no-results">
                      <p>No movies found for your search.</p>
                      <button
                        type="button"
                        className="district-reset-search"
                        onClick={() => setSearchTerm("")}
                      >
                        Clear search
                      </button>
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>
        );

        if (isMobile) {
          return (
            <SwipeableDrawer
              anchor="bottom"
              open={showSearchModal}
              onClose={() => setShowSearchModal(false)}
              onOpen={() => { }}
              swipeAreaWidth={56}
              disableDiscovery={false}
              PaperProps={{
                style: {
                  height: "90vh",
                  borderTopLeftRadius: 24,
                  borderTopRightRadius: 24,
                  overflow: "hidden",
                  background: "transparent",
                  boxShadow: "none"
                },
              }}
            >
              {modalContent}
            </SwipeableDrawer>
          );
        }

        return (
          <Dialog
            open={showSearchModal}
            onClose={() => setShowSearchModal(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
              style: {
                borderRadius: 24,
                margin: "2rem auto",
                width: "90vw",
                maxWidth: "960px",
                maxHeight: "92vh",
                overflow: "hidden",
                background: "transparent",
                boxShadow: "none"
              },
            }}
          >
            {modalContent}
          </Dialog>
        );
      })()}

      <MobileBottomNav
        onOpenSearch={openSearchModal}
        onOpenLocation={() => setShowLocationModal(true)}
      />
    </div>
  );
};

export default CustomNavbar;
